import { getLocale, type Locale } from './locale';

/**
 * Numbers, dates and times, in one place so that there is one answer.
 *
 * Before this file the app formatted in five different ways — a hardcoded
 * `'en-GB'`, three bare `toLocale*` calls that inherited whatever the browser
 * felt like, and one hand-rolled `HH:MM`. That is survivable in one language
 * and not in two, because the choices interact: a chat column that is five
 * characters wide in Latin digits is not five characters wide in Arabic-Indic
 * ones, and a level list that mixes the two makes the learner translate
 * numerals in their head.
 *
 * ## The digit decision
 *
 * **Western digits (0-9) in both languages.** Arabic-Indic digits (٠-٩) are
 * correct Arabic and they are the wrong choice here, for a reason particular
 * to this product: every number a learner sees has a counterpart in the
 * terminal. A commit count, a line number in a stack trace, a level number,
 * `HEAD~3`. The guest is an English Linux printing Western digits, and a
 * course that renders `٣` in the prose beside `3` in the output has invented
 * a translation step at exactly the moment the learner is trying to match one
 * to the other.
 *
 * This is a deliberate departure from what `Intl` would do on its own for
 * `ar`, which is why every formatter below pins the numbering system rather
 * than letting the locale pick. `ar-u-nu-latn` is that pin.
 */

/** The BCP 47 tag each language formats under. See the digit decision above. */
const TAGS: Record<Locale, string> = {
  en: 'en-GB',
  // -u-nu-latn: Arabic language, Western digits.
  ar: 'ar-u-nu-latn',
  // German digits are already Western; what changes is the punctuation —
  // 12.400 and 1,5 — which is what a German reader expects in prose. The
  // terminal beside it prints 1.5; the digits still match, and every German
  // reader of a technical text has met both.
  de: 'de-DE',
  // Japanese digits are Western too, and so is the punctuation: 12,400 and 1.5.
  ja: 'ja-JP',
};

function tag(locale: Locale = getLocale()): string {
  return TAGS[locale];
}

/** The tag for an `Intl` formatter this file does not build itself. */
export function intlTag(locale?: Locale): string {
  return tag(locale);
}

/**
 * `Intl` objects are expensive to build and cheap to keep, and these are
 * called once per row in lists that run to fifty rows. Keyed by tag plus a
 * name for the options, because the options are fixed per formatter.
 */
const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>();

function dateFormat(name: string, options: Intl.DateTimeFormatOptions, locale?: Locale): Intl.DateTimeFormat {
  const key = `${name}:${tag(locale)}`;
  let fmt = cache.get(key) as Intl.DateTimeFormat | undefined;
  if (!fmt) {
    fmt = new Intl.DateTimeFormat(tag(locale), options);
    cache.set(key, fmt);
  }
  return fmt;
}

function numberFormat(name: string, options: Intl.NumberFormatOptions, locale?: Locale): Intl.NumberFormat {
  const key = `n:${name}:${tag(locale)}`;
  let fmt = cache.get(key) as Intl.NumberFormat | undefined;
  if (!fmt) {
    fmt = new Intl.NumberFormat(tag(locale), options);
    cache.set(key, fmt);
  }
  return fmt;
}

/** A plain integer: level numbers, counts, XP. Grouped, so 12,400 reads as one number. */
export function num(value: number, locale?: Locale): string {
  return numberFormat('int', {}, locale).format(value);
}

/** One decimal place, for the two places that show a duration or a size. */
export function decimal(value: number, places = 1, locale?: Locale): string {
  return numberFormat(`d${places}`, { minimumFractionDigits: places, maximumFractionDigits: places }, locale).format(value);
}

/**
 * A download size, as the boot screen says it.
 *
 * The unit is not translated. `MB` is a symbol, it is what every download
 * indicator in every browser says, and "م.ب" would be a puzzle rather than a
 * clarification. The number in front of it is localised; the symbol is not.
 *
 * Isolated, because "in front of it" is not free in Arabic. A digit run is
 * European Number, which the bidi algorithm treats as right-to-left when it
 * decides what to do with the neutrals around it — so the *space* between
 * `5.5` and `MB` takes the paragraph's direction, splits the two apart, and
 * the boot screen renders `MB 5.5`. The isolate below makes the whole phrase
 * one left-to-right run again. Invisible and inert in English.
 */
export function megabytes(bytes: number, locale?: Locale): string {
  return ltr(`${decimal(bytes / 1_000_000, 1, locale)} MB`);
}

/**
 * A price, from the smallest unit of its currency.
 *
 * The Worker keeps the price the way a payment processor wants it — an
 * integer of the currency's minor unit, so 9900 is 99.00 USD
 * (worker/stripe.ts) — and this is the one place that turns that into
 * something to read.
 *
 * **Do not divide by 100 here.** Most currencies have two decimal places and
 * enough of them do not: the Omani rial has three, so the same 9900 is 9.900
 * OMR, and the yen has none, so it is ¥9,900. A hardcoded hundred would
 * misprice a whole country by a factor of ten or a hundred in the direction
 * nobody notices until a customer does. Stripe takes minor units for every
 * currency it supports and `Intl` already knows the exponent for each one, so
 * it is asked rather than assumed. That held while the course was priced in
 * Egyptian pounds and it holds now that it is priced in dollars, which is the
 * point: the currency is the argument that moved, and it moved without this
 * line of code being read by anybody. The project briefs' fees made the same
 * move on 2026-09-28 (the project board, since retired into /tasks): every price on the
 * site is charged in dollars. The same day the page learned to show each
 * reader an approximation in their own currency beside it (`approxMoney`
 * below), which converts and never charges.
 *
 * The fraction is dropped when it is all zeros. A course that costs exactly
 * ninety-nine dollars reads better as `US$99` than as `US$99.00`, and the
 * cents come back the moment there are any — this is a retail price on a
 * landing page, not a line in a ledger.
 *
 * Not wrapped in `ltr()`, unlike `megabytes` above, and the difference is
 * real rather than an oversight. A megabyte figure is a number welded to a
 * Latin symbol that means the same thing in both languages. A price is not:
 * `Intl` decides which side of the number the currency goes on per locale —
 * in Arabic it follows the number, whether it is writing `ج.م.` or `US$` —
 * and it inserts the bidi marks that hold it there. Forcing the whole run
 * left-to-right would take a correctly formatted Arabic price and put the
 * symbol back on the wrong end.
 */
export function money(minorUnits: number, currency: string, locale?: Locale): string {
  const full = numberFormat(`c:${currency}`, { style: 'currency', currency }, locale);
  const places = full.resolvedOptions().maximumFractionDigits ?? 2;
  const whole = minorUnits % 10 ** places === 0;
  const fmt = whole
    ? numberFormat(`c0:${currency}`, { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }, locale)
    : full;
  return fmt.format(minorUnits / 10 ** places);
}

/**
 * A price converted into the reader's own currency, as an approximation:
 * `≈ E£2,590` for US$50 in Cairo.
 *
 * `local` comes from the offer (lib/offer.ts), which gets it from the Worker:
 * the currency of the country the request came from and a daily rate
 * (worker/local-currency.ts). The charge is still in `currency`, so wherever
 * this is shown the exact figure is shown too — this is what it is *about* in
 * the reader's money, and it is rounded to say so. Three significant figures
 * from a thousand up (2,589.14 is 2,590), whole units from ten, and the
 * currency's own decimals below that: a converted price quoted to the piastre
 * claims a precision the reader's bank will not honour.
 *
 * The same minor-unit rule as `money` above, on both sides: the exponent of
 * each currency is asked of `Intl`, never assumed to be two.
 */
export function approxMoney(
  minorUnits: number,
  currency: string,
  local: { currency: string; rate: number },
  locale?: Locale,
): string {
  const from = numberFormat(`c:${currency}`, { style: 'currency', currency }, locale).resolvedOptions().maximumFractionDigits ?? 2;
  const value = (minorUnits / 10 ** from) * local.rate;
  const to = numberFormat(`c:${local.currency}`, { style: 'currency', currency: local.currency }, locale).resolvedOptions().maximumFractionDigits ?? 2;
  let rounded: number;
  let places: number;
  if (value >= 1000) {
    const step = 10 ** (Math.floor(Math.log10(value)) - 2);
    rounded = Math.round(value / step) * step;
    places = 0;
  } else if (value >= 10) {
    rounded = Math.round(value);
    places = 0;
  } else {
    places = to;
    rounded = Number(value.toFixed(to));
  }
  const fmt = numberFormat(
    `a${places}:${local.currency}`,
    { style: 'currency', currency: local.currency, minimumFractionDigits: places, maximumFractionDigits: places },
    locale,
  );
  return `≈ ${fmt.format(rounded)}`;
}

/**
 * A price where there is room for one line: the reader's currency first when
 * there is one, and the exact figure beside it, `≈ E£181 (US$3.50)`. Just
 * `money` when there is nothing to convert.
 */
export function moneyForReader(
  minorUnits: number,
  currency: string,
  local: { currency: string; rate: number } | null | undefined,
  locale?: Locale,
): string {
  const exact = money(minorUnits, currency, locale);
  return local ? `${approxMoney(minorUnits, currency, local, locale)} (${exact})` : exact;
}

/** `12 Mar` — the label on a commit row. */
export function shortDate(date: Date, locale?: Locale): string {
  return dateFormat('short', { month: 'short', day: 'numeric' }, locale).format(date);
}

/** A full date and time, for the commit detail panel. */
export function fullDateTime(date: Date, locale?: Locale): string {
  return dateFormat('full', { dateStyle: 'medium', timeStyle: 'short' }, locale).format(date);
}

/**
 * `14:32`, always, in both languages.
 *
 * Pinned to 24 hours rather than left to the locale, because the chat message
 * list sets these in a fixed-width column and lays the whole thread out
 * against it. A 12-hour clock is five characters some of the time and eight
 * others, and the column would breathe in and out down the length of the
 * conversation. `hourCycle: 'h23'` is what keeps `09:05` from becoming `9:05`.
 */
export function clockTime(date: Date, locale?: Locale): string {
  return dateFormat('clock', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }, locale).format(date);
}

/** `Mon 3 Mar` — the day separator in the chat list, without the year. */
export function dayLabel(date: Date, locale?: Locale): string {
  return dateFormat('day', { weekday: 'short', day: 'numeric', month: 'short' }, locale).format(date);
}

/** The same, with the year, for a day in a previous one. */
export function dayLabelWithYear(date: Date, locale?: Locale): string {
  return dateFormat('dayYear', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }, locale).format(date);
}

/**
 * `15 Nov 2026` — a date on a calendar rather than a day in a conversation.
 *
 * The weekday is what separates this from `dayLabelWithYear`, and dropping it
 * is the whole point. A chat needs "Mon" because the reader is placing a
 * message in their own week; a deadline on a project brief is a date somebody
 * writes down, where the weekday is noise in a column narrow enough to wrap
 * because of it.
 */
export function calendarDate(date: Date, locale?: Locale): string {
  return dateFormat('calendar', { day: 'numeric', month: 'short', year: 'numeric' }, locale).format(date);
}

/**
 * `1 January 2027`, read on the UTC calendar rather than the reader's.
 *
 * For a moment that is defined in UTC, like the end of the launch sale
 * (worker/stripe.ts, `coursePrice`): midnight UTC on 1 January is the
 * evening of 31 December in the Americas, and a page there saying "until
 * 31 Dec" would name a day the sale is not defined by.
 */
export function utcDate(date: Date, locale?: Locale): string {
  return dateFormat('utcDate', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }, locale).format(date);
}

/**
 * Wrap a machine string so bidi cannot rearrange it.
 *
 * A path, a branch name, a hash or a command sitting inside an Arabic
 * sentence is a run of left-to-right text inside a right-to-left paragraph,
 * and the Unicode bidi algorithm will do its best with it — which for
 * anything ending in punctuation, or containing a slash and a dot, is not
 * good enough. `src/cart.js` can come out as `cart.js/src`, and a learner
 * cannot tell that it is the renderer lying rather than the level.
 *
 * U+2066 LEFT-TO-RIGHT ISOLATE … U+2069 POP DIRECTIONAL ISOLATE says "this
 * run is left-to-right, and it is one thing" — the isolate, unlike the older
 * embedding, also stops the run from changing how the text *around* it is
 * ordered. Use it for a machine string that has to sit inside prose. For a
 * whole element, prefer `dir="ltr"` in the markup, which is cheaper and does
 * not travel with the string when it is copied.
 */
export function ltr(value: string): string {
  return `⁦${value}⁩`;
}
